import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  CardContent,
  Typography, 
  Chip, 
  CircularProgress, 
  Alert,
  Paper,
  Grid,
  Fade,
  Divider
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { protectedResources, scopes } from "../../authConfig";
import useFetchWithMsal from '../../hooks/useFetchWithMsal';

// authConfigからエンドポイントを参照するように変更
const API_ENDPOINTS = {
    getActiveSeason: protectedResources.getActiveSeason.endpoint,
    getCompanySeasonEntries: protectedResources.getCompanySeasonEntries.endpoint
};

const ActiveSeasonStatus = ({ onLoadingChange }) => {
    const [activeSeason, setActiveSeason] = useState(null);
    const [entryStatus, setEntryStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyId, setCompanyId] = useState(null);
    const { instance } = useMsal();
    const navigate = useNavigate();
    
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });    

    // ローディング状態が変化したら親コンポーネントに通知
    useEffect(() => {
        // onLoadingChangeプロパティが渡されている場合のみ呼び出す
        if (onLoadingChange) {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    // クリック時の遷移処理
    const handleCardClick = () => {
        if (!activeSeason) return;
        
        // エントリー受付中でエントリーなしの場合はEntryStatusへ
        // エントリーがある場合はEntryStatusへ遷移
        if (!entryStatus) {
            // シーズンIDを使って新規エントリー作成ページへ遷移
            navigate(`/entry-status`);
        } else {
            // エントリーIDを使って既存のエントリー編集ページへ遷移
            navigate(`/entry/${entryStatus.id}/edit`);
        }
    };

    useEffect(() => {
        // 会社IDの取得
        if (instance) {
            const activeAccount = instance.getActiveAccount();
            if (activeAccount && activeAccount.idTokenClaims?.extension_CompanyId) {
                setCompanyId(activeAccount.idTokenClaims.extension_CompanyId);
            }
        }
    }, [instance]);

    // アクティブなシーズンとエントリー状況を取得
    useEffect(() => {
        const fetchActiveSeason = async () => {
            if (!companyId) return;
            
            setLoading(true);
            // 親コンポーネントにローディング開始を通知
            if (onLoadingChange) {
                onLoadingChange(true);
            }
            
            try {
                // 新しいアクティブシーズン取得APIを使用
                const seasonResponse = await execute(
                    'GET',
                    `${API_ENDPOINTS.getActiveSeason}?resolveRelations=true`
                );
                
                if (seasonResponse) {
                    setActiveSeason(seasonResponse);
                    
                    // 新しい企業別シーズンエントリー一覧取得APIを使用してエントリー状況を取得
                    const entryResponse = await execute(
                        'GET',
                        `${API_ENDPOINTS.getCompanySeasonEntries}?companyId=${companyId}&seasonId=${seasonResponse.id}`
                    );
                    
                    if (Array.isArray(entryResponse) && entryResponse.length > 0) {
                        setEntryStatus(entryResponse[0]);
                    } else {
                        setEntryStatus(null);
                    }
                } else {
                    setActiveSeason(null);
                }
            } catch (error) {
                console.error('シーズンデータ取得エラー:', error);
                setError('シーズン情報の取得に失敗しました');
            } finally {
                setLoading(false);
                // 親コンポーネントにローディング終了を通知
                if (onLoadingChange) {
                    onLoadingChange(false);
                }
            }
        };
        
        fetchActiveSeason();    }, [execute, companyId, onLoadingChange]);
    
    // エントリー状態に応じた表示名を返す関数
    const getStatusLabel = (status) => {
        switch (status) {
            case 'approved': return '承認済み';
            case 'rejected': return '却下';
            case 'pending': return '審査中';
            default: return 'エントリーなし';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }

    if (!activeSeason) {
        return null; // エントリー期間中のシーズンがない場合は何も表示しない
    }

    return (
        <Box mb={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CampaignIcon color="primary" /> エントリー受付中のプログラム
            </Typography>
              <Fade in={true} timeout={800}>
                <Paper 
                    elevation={4} 
                    onClick={handleCardClick}
                    sx={{
                        background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        position: 'relative',
                        mb: 2,
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    {/* アクセントとなる装飾パターン */}
                    <Box 
                        sx={{ 
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '120px',
                            height: '120px',
                            borderRadius: '0 0 0 100%',
                            background: 'rgba(255,255,255,0.1)',
                        }}
                    />
                    
                    <CardContent sx={{ color: 'white', p: 3, position: 'relative', zIndex: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {activeSeason.seasonName}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12} sm={8}>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <CalendarTodayIcon fontSize="small" />
                                    <Typography variant="body1">
                                        エントリー期間: {new Date(activeSeason.acceptanceStartDate).toLocaleDateString()} 〜 {new Date(activeSeason.acceptanceEndDate).toLocaleDateString()}
                                    </Typography>
                                </Box>
                                
                                {entryStatus && (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PersonIcon fontSize="small" />
                                        <Typography variant="body1">
                                            申込枠: <strong>{entryStatus.requestedEntryCount || 0}</strong> 名 / 
                                            承認枠: <strong>{entryStatus.approvedEntryCount || 0}</strong> 名
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            
                            <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
                                <Chip 
                                    label="エントリー受付中" 
                                    sx={{ 
                                        backgroundColor: 'white',
                                        color: '#1976d2',
                                        fontWeight: 'bold',
                                        px: 1,
                                    }}
                                />
                                
                                {entryStatus ? (
                                    <Chip 
                                        label={getStatusLabel(entryStatus.status)} 
                                        sx={{ 
                                            backgroundColor: entryStatus.status === 'approved' ? '#4caf50' : 
                                                          entryStatus.status === 'rejected' ? '#f44336' : 
                                                          entryStatus.status === 'pending' ? '#ff9800' : '#e0e0e0',
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                ) : (
                                    <Chip 
                                        label="エントリーなし" 
                                        sx={{ 
                                            backgroundColor: 'rgba(255,255,255,0.3)',
                                            color: 'white',
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        
                        {/* シーズンの詳細情報がある場合はここに表示 */}
                        {activeSeason.description && (
                            <Box mt={2}>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 1 }} />
                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    {activeSeason.description}
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Paper>
            </Fade>
        </Box>
    );
};

export default ActiveSeasonStatus;